import {Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {SignAction} from '@shared/components/core-sign/models/sign-action';
import {FirstStepInfo, SignStepperStep} from '@shared/components/core-sign/models/sign-stepper-step';
import {FileModel} from '../../../authority/authority-shared/models/enums.model';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {PaperSignMotivationDialogComponent} from '@shared/components/core-sign/components/paper-sign-motivation-dialog/paper-sign-motivation-dialog.component';
import {DigitalSignAttachmentModel} from '@shared/components/core-sign/models/digital-sign-attachment.model';
import {DigitalSignEventsService} from '@shared/components/core-sign/services/digital-sign-events.service';
import {Subscription} from 'rxjs';
import { TranslateContext, TranslatePipe } from '@shared/pipes/translate.pipe';

@Component({
	selector: 'amd-core-sign',
	templateUrl: './core-sign.component.html',
	styleUrls: ['./core-sign.component.scss']
})
export class CoreSignComponent implements OnInit, OnDestroy {

	@Input() module: string;
	@Input() enableDigitalSign: boolean;
	@Input() isFirstStepValid: boolean;
	@Input() requireAttachment: boolean = false;
	@Input() onCompleteAction: SignAction;
	@Input() firstStepInfo: FirstStepInfo;
	@Input() emptyPaperSignFile: FileModel;
	@Input() uploadedFiles: FileModel[];
	@Input() actionFinish: SignAction;
	@Input() attachments: DigitalSignAttachmentModel[] = [];
	@Input() isPaperSign?: boolean;
	@Output() onCancelSignProcess: EventEmitter<void> = new EventEmitter<void>();
	@Output() onDownloadSignPDF: EventEmitter<void> = new EventEmitter<void>();
	@Output() onFileUpload: EventEmitter<File> = new EventEmitter<File>();
	@Output() onRemoveFile: EventEmitter<FileModel> = new EventEmitter<FileModel>();
	@Output() onRemoveAttachedFile: EventEmitter<FileModel> = new EventEmitter<FileModel>();
	@Output() onStepChanged: EventEmitter<{ prev: number, current: number }> = new EventEmitter<{ prev: number, current: number }>();
	@Output() paperSignMotivation?: EventEmitter<string> = new EventEmitter<string>();

	steps: SignStepperStep[];
	visibleSteps: SignStepperStep[];
	currentStep: SignStepperStep;
	dialog: MatDialog;
	digitalSignEventService: DigitalSignEventsService;
	hasDonePaperSign: boolean = false;
	canGoToNextStepSubscription: Subscription;
	canGoDirectlyToDigitalSign: Subscription;
	translateContext = TranslateContext;


	constructor(
		private injector: Injector,
		private translatePipe: TranslatePipe
	) {
	}

	get isFinished(): boolean {
		return this.actionFinish && this.actionFinish.visible();
	}

	get is_paper_sign(): boolean {
		return this.isPaperSign;
	}

	ngOnInit(): void {
		if (this.enableDigitalSign) {
			this.dialog = this.injector.get(MatDialog);
			this.digitalSignEventService = this.injector.get(DigitalSignEventsService);
			this.canGoToNextStepSubscription = this.digitalSignEventService.canGoToNextStepObservable.subscribe(result => {
				if (result) {
					this.nextStep();
				}
			});
			this.canGoDirectlyToDigitalSign =
				this.digitalSignEventService.goDirectlyToDigitalSign.subscribe(
					() => {
						this.currentStep = this.visibleSteps[2];
						this.onStepChanged.emit({prev : 0, current: this.currentStep.index});
					}
				);

		}

		const cancelSignProcessAction: SignAction = {
			action: this.cancelSignProcess.bind(this),
			cta: this.translatePipe.transform('ANNULLA_PROCESSO_DI_FIRMA', TranslateContext.SHARED),
			icon: 'amd-icon--rifiuta-16x16',
			btnClass: 'amd-bottom-nav__button amd-bottom-nav__button--secondary mat-button amd-color-red',
			enabled: () => true,
			visible: () => true
		};
		const prevStep: SignAction = {
			action: this.prevStep.bind(this),
			cta: this.translatePipe.transform('STEP_PRECENDENTE', TranslateContext.SHARED),
			icon: 'amd-icon--arrow-left-16x16',
			btnClass: 'amd-button amd-button--reverse mat-button',
			visible: () => true,
			enabled: () => true,
		};
		const nextStep: SignAction = {
			action: this.nextStep.bind(this),
			cta: this.translatePipe.transform('STEP_SUCCESSIVO', TranslateContext.SHARED),
			icon: 'amd-icon--arrow-right-16x16',
			btnClass: 'amd-button amd-button--reverse mat-button',
			colClass: 'u-float--right',
			visible: () => true,
			enabled: () => true,
		};

		this.steps = [{
			...{
				index: 0,
				show: true,
				actions: [
					cancelSignProcessAction,
					{
						...nextStep,
						cta: this.translatePipe.transform((this.enableDigitalSign ? 'PROSEGUI_CON_LA_PROCEDURA' : 'PROCEDI_CON_LA_FIRMA'), TranslateContext.SHARED),
						enabled: () => this.isFirstStepValid
					},
				]
			}, ...this.firstStepInfo
		}, {
			index: 1,
			name: this.translatePipe.transform('PROCEDURA', TranslateContext.SHARED),
			title: this.translatePipe.transform('PROCEDURA_FIRMA', TranslateContext.SHARED),
			show: this.enableDigitalSign,
			actions: [
				{...prevStep, cta: this.translatePipe.transform('TORNA_ALL_ANAGRAFICA', TranslateContext.SHARED)},
				cancelSignProcessAction,
				{...nextStep, cta: this.translatePipe.transform('PROCEDI_CON_LA_FIRMA', TranslateContext.SHARED), enabled: () => false}
			]
		}, {
			index: 2,
			name: this.translatePipe.transform('FIRMA', TranslateContext.SHARED),

			// è problematico, perché viene eseguito all'onInit
			// e ancora non sappiamo se l'utente sceglie la firma digitale o cartacea
			title: this.translatePipe.transform((!this.is_paper_sign ? 'FIRMA_DIGITALE' : 'FIRMA_CARTACEA'), TranslateContext.SHARED),
			// title: this.translatePipe.transform((this.enableDigitalSign ? 'FIRMA_DIGITALE' : 'FIRMA_CARTACEA'), TranslateContext.SHARED),
			show: true,
			icon: this.enableDigitalSign ? '--firma-digitale-24x24' : '--firma-cartacea_24x24',
			actions: [
				{...prevStep, cta: this.translatePipe.transform((this.enableDigitalSign ? 'TORNA_ALLA_PROCEDURA' : 'TORNA_ALL_ANAGRAFICA'), TranslateContext.SHARED)},
				cancelSignProcessAction,
				{...this.onCompleteAction, visible: () => this.enableDigitalSign || !!(this.uploadedFiles && this.uploadedFiles.length)}
			]
		}];

		this.visibleSteps = this.steps.filter(s => s.show);
		this.currentStep = this.steps[0];
	}

	ngOnDestroy(): void {
		if (!!this.canGoToNextStepSubscription) {
			this.canGoToNextStepSubscription.unsubscribe();
		}

		if (!!this.canGoDirectlyToDigitalSign) {
			this.canGoDirectlyToDigitalSign.unsubscribe();
		}
	}

	cancelSignProcess(): void {
		this.onCancelSignProcess.emit();
	}

	downloadSignPDF(): void {
		this.onDownloadSignPDF.emit();
	}

	nextStep(): void {
		const prev: number = this.currentStep.index;

		if (this.currentStep.index === 1.5 && this.enableDigitalSign) {
			this.currentStep = this.visibleSteps[2] || this.currentStep;
			return;
		}

		if (this.currentStep.index === 1 && this.enableDigitalSign) {
			this.currentStep.index = 1.5;
			this.onStepChanged.emit({prev: 1.5, current: 1.5});
			return;
		}

		this.currentStep = this.visibleSteps[this.visibleSteps.indexOf(this.currentStep) + 1] || this.currentStep;
		if (prev !== this.currentStep.index) {
			this.onStepChanged.emit({prev, current: this.currentStep.index});
		}
	}

	prevStep(): void {
		const prev: number = this.currentStep.index;
		this.currentStep = this.visibleSteps[this.visibleSteps.indexOf(this.currentStep) - 1] || this.currentStep;
		if (prev !== this.currentStep.index) {
			this.onStepChanged.emit({prev, current: this.currentStep.index});
		}

		if (this.hasDonePaperSign) {
			this.hasDonePaperSign = false;
		}
	}

	onFileUploaded(files: File[]): void {
		this.onFileUpload.emit(files[0]);
	}

	removeUploadedFile(file: FileModel): void {
		this.onRemoveFile.emit(file);
	}

	goToPaperSign(): void {
		const dialogRef: MatDialogRef<PaperSignMotivationDialogComponent> = this.dialog.open(PaperSignMotivationDialogComponent, {
			width: '800px',
		});
		dialogRef.afterClosed().subscribe(result => {
			if (!!result && !!result.txt_message) {
				this.paperSignMotivation.emit(result.txt_message);
				this.nextStep();
				this.hasDonePaperSign = true;
			}
		});
	}

	canGoToNextStep(event: boolean): void {
		if (this.enableDigitalSign) {
			this.steps.find(step => step.index === 1)
				.actions.find(action => action.cta === this.translatePipe.transform('PROCEDI_CON_LA_FIRMA', TranslateContext.SHARED))
				.enabled = () => event;
		}
	}
}
